import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { COLORS } from "./GlobalStyle";

const hex = (c) => new THREE.Color(c);

export default function Scene3D({ mode = "particles", data, className = "", height }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth || 1;
    let heightPx = mount.clientHeight || 1;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / heightPx, 0.1, 100);
    camera.position.set(0, 0, 9);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, heightPx);
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(4, 6, 8);
    const point = new THREE.PointLight(hex(COLORS.amber), 1.1, 20);
    point.position.set(-4, 3, 5);
    scene.add(ambient, dir, point);

    const group = new THREE.Group();
    scene.add(group);

    const disposables = [];
    let animate = () => {};
    let onScroll = null;

    const mkMat = (color, opts = {}) =>
      new THREE.MeshStandardMaterial({ color: hex(color), roughness: 0.35, metalness: 0.25, ...opts });

    if (mode === "particles") {
      const count = 260;
      const positions = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 16;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 10 - 2;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({ color: hex(COLORS.indigo), size: 0.06, transparent: true, opacity: 0.55 });
      const points = new THREE.Points(geo, mat);
      group.add(points);
      disposables.push(geo, mat);
      camera.position.z = 7;
      animate = (t) => {
        points.rotation.y = t * 0.00006;
        points.rotation.x = Math.sin(t * 0.00004) * 0.08;
      };
    } else if (mode === "gate") {
      // Flat "comic" materials + a cheap cartoon ink-outline trick (a slightly
      // scaled backside-only clone behind each mesh) so the scene matches the
      // site's bold, thick-bordered illustration style instead of looking like
      // plain grey/dark PBR boxes.
      const outlineMat = new THREE.MeshBasicMaterial({ color: hex(COLORS.ink), side: THREE.BackSide });
      disposables.push(outlineMat);
      const addOutline = (mesh, scale = 1.07) => {
        const o = new THREE.Mesh(mesh.geometry, outlineMat);
        o.scale.setScalar(scale);
        mesh.add(o);
      };
      const flatMat = (color) => {
        const m = new THREE.MeshLambertMaterial({ color: hex(color) });
        disposables.push(m);
        return m;
      };
      const trackGeo = (geo) => { disposables.push(geo); return geo; };

      // Road strip with a dashed centre line
      const road = new THREE.Mesh(trackGeo(new THREE.PlaneGeometry(12, 4.6)), flatMat(COLORS.ink));
      road.material.transparent = true;
      road.material.opacity = 0.07;
      road.rotation.x = -Math.PI / 2.3;
      road.position.y = -1.5;
      group.add(road);

      const dashMat = flatMat(COLORS.ink);
      dashMat.transparent = true;
      dashMat.opacity = 0.18;
      const dashGeo = trackGeo(new THREE.BoxGeometry(0.45, 0.02, 0.14));
      for (let dx = -4.4; dx <= 4.4; dx += 1.3) {
        const dash = new THREE.Mesh(dashGeo, dashMat);
        dash.position.set(dx, -1.49, 0.35);
        group.add(dash);
      }

      // Gate posts with a little amber cap
      const postGeo = trackGeo(new THREE.CylinderGeometry(0.2, 0.24, 2.4, 12));
      const capGeo = trackGeo(new THREE.SphereGeometry(0.26, 14, 14));
      const capMat = flatMat(COLORS.amber);
      const makePost = (x) => {
        const post = new THREE.Mesh(postGeo, flatMat(COLORS.indigo));
        post.position.set(x, -0.35, 0);
        addOutline(post);
        const cap = new THREE.Mesh(capGeo, capMat);
        cap.position.set(0, 1.28, 0);
        post.add(cap);
        addOutline(cap, 1.1);
        return post;
      };
      const postL = makePost(-2.7);
      const postR = makePost(2.7);
      group.add(postL, postR);

      // Striped boom barrier, pivoting up from the left post
      const armPivot = new THREE.Group();
      armPivot.position.set(-2.7, 1.0, 0);
      const stripes = 6;
      const armLen = 2.8;
      const segLen = armLen / stripes;
      for (let i = 0; i < stripes; i++) {
        const seg = new THREE.Mesh(
          trackGeo(new THREE.BoxGeometry(segLen * 0.92, 0.26, 0.14)),
          flatMat(i % 2 === 0 ? COLORS.amber : COLORS.paper)
        );
        seg.position.set(segLen * (i + 0.5), 0, 0);
        addOutline(seg, 1.12);
        armPivot.add(seg);
      }
      const counterweight = new THREE.Mesh(trackGeo(new THREE.BoxGeometry(0.34, 0.34, 0.34)), flatMat(COLORS.ink));
      counterweight.position.set(-0.3, 0, 0);
      addOutline(counterweight, 1.12);
      armPivot.add(counterweight);
      group.add(armPivot);

      // A small, friendly low-poly car
      const carGroup = new THREE.Group();
      const body = new THREE.Mesh(trackGeo(new THREE.BoxGeometry(1.7, 0.48, 0.82)), flatMat(COLORS.sky));
      body.position.y = 0.05;
      addOutline(body);
      carGroup.add(body);

      const cabin = new THREE.Mesh(trackGeo(new THREE.BoxGeometry(0.92, 0.4, 0.68)), flatMat(COLORS.paper));
      cabin.position.set(-0.08, 0.42, 0);
      addOutline(cabin);
      carGroup.add(cabin);

      const wheelGeo = trackGeo(new THREE.CylinderGeometry(0.22, 0.22, 0.2, 16));
      const wheelMat = flatMat(COLORS.ink);
      const wheelSpots = [
        [0.55, -0.24, 0.42], [0.55, -0.24, -0.42],
        [-0.55, -0.24, 0.42], [-0.55, -0.24, -0.42],
      ];
      const wheels = wheelSpots.map(([x, y, z]) => {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.rotation.z = Math.PI / 2;
        w.position.set(x, y, z);
        addOutline(w, 1.15);
        carGroup.add(w);
        return w;
      });

      carGroup.position.set(-4.3, -0.82, 0.15);
      group.add(carGroup);

      camera.position.set(0, 0.3, 8.4);

      // Play the "gate opens, car drives through" beat once the scene is
      // actually visible (not tied to raw page-scroll position, which made
      // the old version look half-finished no matter where you loaded the
      // page), then settle into a subtle idle loop.
      let startTime = null;
      let played = false;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !played) {
              played = true;
              startTime = performance.now();
            }
          });
        },
        { threshold: 0.3 }
      );
      io.observe(mount);
      disposables.push({ dispose: () => io.disconnect() });

      const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
      animate = (t) => {
        const progress = startTime == null ? 0 : Math.min((t - startTime) / 1500, 1);
        const eased = easeOutCubic(progress);
        armPivot.rotation.z = eased * 1.05;
        carGroup.position.x = -4.3 + eased * 8.1;
        if (progress > 0) wheels.forEach((w) => { w.rotation.x -= 0.1; });
        carGroup.position.y = -0.82 + Math.sin(t * 0.0016) * 0.02;
      };
    } else if (mode === "friction") {
      // Two gears grinding against each other, throwing off sparks at the
      // contact point — visualizes "stuck manual friction" for the Why page.
      const outlineMat = new THREE.MeshBasicMaterial({ color: hex(COLORS.ink), side: THREE.BackSide });
      disposables.push(outlineMat);
      const addOutline = (mesh, scale = 1.08) => {
        const o = new THREE.Mesh(mesh.geometry, outlineMat);
        o.scale.setScalar(scale);
        mesh.add(o);
      };
      const flatMat = (color) => {
        const m = new THREE.MeshLambertMaterial({ color: hex(color) });
        disposables.push(m);
        return m;
      };
      const trackGeo = (g) => { disposables.push(g); return g; };

      const makeGear = (radius, teeth, color) => {
        const gearGroup = new THREE.Group();
        const hub = new THREE.Mesh(trackGeo(new THREE.CylinderGeometry(radius, radius, 0.32, 24)), flatMat(color));
        hub.rotation.x = Math.PI / 2;
        addOutline(hub, 1.05);
        gearGroup.add(hub);
        const toothGeo = trackGeo(new THREE.BoxGeometry(radius * 0.32, radius * 0.34, 0.32));
        const toothMat = flatMat(color);
        for (let i = 0; i < teeth; i++) {
          const angle = (i / teeth) * Math.PI * 2;
          const tooth = new THREE.Mesh(toothGeo, toothMat);
          tooth.position.set(Math.cos(angle) * (radius + radius * 0.15), Math.sin(angle) * (radius + radius * 0.15), 0);
          tooth.rotation.z = angle;
          addOutline(tooth, 1.18);
          gearGroup.add(tooth);
        }
        const bolt = new THREE.Mesh(
          trackGeo(new THREE.CylinderGeometry(radius * 0.24, radius * 0.24, 0.36, 16)),
          flatMat(COLORS.ink)
        );
        bolt.rotation.x = Math.PI / 2;
        gearGroup.add(bolt);
        return gearGroup;
      };

      const gearA = makeGear(1.15, 10, COLORS.indigo);
      gearA.position.set(-1.0, 0, 0);
      group.add(gearA);

      const gearB = makeGear(0.78, 8, COLORS.sky);
      gearB.position.set(1.22, 0.5, 0);
      group.add(gearB);

      const sparkCount = 36;
      const sparkGeo = new THREE.BufferGeometry();
      const sparkPos = new Float32Array(sparkCount * 3).fill(-9);
      sparkGeo.setAttribute("position", new THREE.BufferAttribute(sparkPos, 3));
      const sparkMat = new THREE.PointsMaterial({ color: hex(COLORS.amber), size: 0.1, transparent: true, opacity: 0.95 });
      const sparks = new THREE.Points(sparkGeo, sparkMat);
      group.add(sparks);
      disposables.push(sparkGeo, sparkMat);
      const sparkVel = Array.from({ length: sparkCount }, () => ({ x: 0, y: 0, life: 0 }));
      const meshPoint = new THREE.Vector3(0.14, 0.26, 0.18);

      camera.position.set(0, 0, 6.6);
      let nextBurst = 500;
      animate = (t) => {
        gearA.rotation.z += 0.013;
        gearB.rotation.z -= 0.013 * (1.15 / 0.78);

        if (t > nextBurst) {
          nextBurst = t + 650 + Math.random() * 500;
          for (let n = 0; n < 6; n++) {
            const i = Math.floor(Math.random() * sparkCount);
            sparkPos[i * 3] = meshPoint.x + (Math.random() - 0.5) * 0.12;
            sparkPos[i * 3 + 1] = meshPoint.y + (Math.random() - 0.5) * 0.12;
            sparkPos[i * 3 + 2] = meshPoint.z;
            sparkVel[i] = { x: (Math.random() - 0.5) * 0.02, y: Math.random() * 0.03, life: 1 };
          }
        }
        for (let i = 0; i < sparkCount; i++) {
          if (sparkVel[i].life > 0) {
            sparkPos[i * 3] += sparkVel[i].x;
            sparkPos[i * 3 + 1] += sparkVel[i].y;
            sparkVel[i].y -= 0.0018;
            sparkVel[i].life -= 0.022;
            if (sparkVel[i].life <= 0) sparkPos[i * 3 + 1] = -9;
          }
        }
        sparkGeo.attributes.position.needsUpdate = true;
      };
    } else if (mode === "pillars") {
      const items = data && data.length ? data : [1, 2, 3, 4, 5];
      const shapeFns = [
        () => new THREE.IcosahedronGeometry(0.62, 0),
        () => new THREE.OctahedronGeometry(0.68, 0),
        () => new THREE.TorusGeometry(0.5, 0.2, 12, 24),
        () => new THREE.ConeGeometry(0.55, 1, 8),
        () => new THREE.DodecahedronGeometry(0.6, 0),
      ];
      const palette = [COLORS.amber, COLORS.indigo, COLORS.sky, COLORS.cyan, COLORS.amber];
      const meshes = items.map((_, i) => {
        const geo = shapeFns[i % shapeFns.length]();
        const mat = mkMat(palette[i % palette.length]);
        const mesh = new THREE.Mesh(geo, mat);
        const angle = (i / items.length) * Math.PI * 2;
        mesh.position.set(Math.cos(angle) * 2.6, Math.sin(angle) * 1.4, 0);
        group.add(mesh);
        disposables.push(geo, mat);
        return mesh;
      });
      camera.position.set(0, 0, 8);
      animate = (t) => {
        group.rotation.y = t * 0.00012;
        meshes.forEach((m, i) => {
          m.rotation.x = t * 0.0006 + i;
          m.rotation.y = t * 0.0004 + i;
        });
      };
    } else if (mode === "bars") {
      const values = (data && data.length ? data : [0.6, 0.8, 0.4, 0.9]).map(Number);
      const max = Math.max(...values, 1);
      const palette = [COLORS.amber, COLORS.indigo, COLORS.sky, COLORS.cyan];
      const gap = 1.5;
      camera.position.set(2, 2.4, 9);
      camera.lookAt(0, 0, 0);
      values.forEach((v, i) => {
        const h = (v / max) * 4 + 0.4;
        const geo = new THREE.BoxGeometry(0.9, h, 0.9);
        const mat = mkMat(palette[i % palette.length]);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set((i - (values.length - 1) / 2) * gap, h / 2 - 2, 0);
        group.add(mesh);
        disposables.push(geo, mat);
      });
      animate = (t) => {
        group.rotation.y = Math.sin(t * 0.0002) * 0.35;
      };
    } else if (mode === "path") {
      const steps = (data && data.length ? data : [1, 2, 3, 4]).length;
      const curvePts = [];
      for (let i = 0; i < steps; i++) {
        const x = (i - (steps - 1) / 2) * 2.4;
        const y = Math.sin(i * 1.1) * 0.9;
        curvePts.push(new THREE.Vector3(x, y, 0));
      }
      const curve = new THREE.CatmullRomCurve3(curvePts);
      const tubeGeo = new THREE.TubeGeometry(curve, 64, 0.06, 8, false);
      const tubeMat = mkMat(COLORS.sky, { metalness: 0.1 });
      const tube = new THREE.Mesh(tubeGeo, tubeMat);
      group.add(tube);
      disposables.push(tubeGeo, tubeMat);
      const nodes = curvePts.map((p, i) => {
        const geo = new THREE.SphereGeometry(0.24, 20, 20);
        const mat = mkMat(i % 2 === 0 ? COLORS.amber : COLORS.indigo);
        const node = new THREE.Mesh(geo, mat);
        node.position.copy(p);
        group.add(node);
        disposables.push(geo, mat);
        return node;
      });
      camera.position.set(0, 1, 7);
      animate = (t) => {
        group.rotation.y = Math.sin(t * 0.00015) * 0.25;
        nodes.forEach((n, i) => {
          n.position.y = curvePts[i].y + Math.sin(t * 0.002 + i) * 0.08;
        });
      };
    } else if (mode === "badge") {
      const geo = new THREE.TorusKnotGeometry(1, 0.32, 140, 20);
      const mat = mkMat(COLORS.indigo, { metalness: 0.4, roughness: 0.25 });
      const knot = new THREE.Mesh(geo, mat);
      group.add(knot);
      disposables.push(geo, mat);
      camera.position.set(0, 0, 7);
      animate = (t) => {
        knot.rotation.x = t * 0.0004;
        knot.rotation.y = t * 0.0006;
      };
    }

    let mx = 0;
    let my = 0;
    const onMouseMove = (e) => {
      const rect = mount.getBoundingClientRect();
      mx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      my = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    mount.addEventListener("mousemove", onMouseMove);

    let raf;
    const loop = (t) => {
      animate(t);
      camera.position.x += (mx * 0.6 - camera.position.x) * 0.03;
      camera.position.y += (-my * 0.4 - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => {
      width = mount.clientWidth || 1;
      heightPx = mount.clientHeight || 1;
      camera.aspect = width / heightPx;
      camera.updateProjectionMatrix();
      renderer.setSize(width, heightPx);
    });
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mount.removeEventListener("mousemove", onMouseMove);
      if (onScroll) window.removeEventListener("scroll", onScroll);
      disposables.forEach((d) => d.dispose && d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [mode, data]);

  return <div ref={mountRef} className={`scene3d ${className}`} style={height ? { height } : undefined} />;
}